/**
 * Tracking Composable
 *
 * Provides a simple interface to the TrackingService for use in Vue components.
 * This is a wrapper around the TrackingService singleton.
 */

import { TrackingService } from '@/services/tracking'
import { TrackingEventType } from '@monorepo/types'
import type { EngagementEvent, MonetizationEvent } from '@monorepo/types'

// Singleton instance
let trackingServiceInstance: TrackingService | null = null

function getTrackingService(): TrackingService {
  if (!trackingServiceInstance) {
    trackingServiceInstance = new TrackingService()
  }
  return trackingServiceInstance
}

/**
 * Use tracking service in components
 */
export function useTracking() {
  const service = getTrackingService()

  return {
    /**
     * Track an engagement event
     */
    trackEngagementEvent: (event: Omit<EngagementEvent, 'timestamp'>) => {
      service.trackEngagementEvent(event)
    },

    /**
     * Track a monetization event
     */
    trackMonetizationEvent: (event: Omit<MonetizationEvent, 'timestamp'>) => {
      service.trackMonetizationEvent(event)
    },

    /**
     * Track a CTA click event
     */
    trackCTAClick: (data: { ctaText: string; ctaType?: string; metadata?: Record<string, string | number | boolean> }) => {
      service.trackCTAClick({
        eventType: TrackingEventType.CTA_CLICKED,
        ctaType: data.ctaType || 'button',
        ctaText: data.ctaText,
        metadata: data.metadata,
      })
    },
  }
}
